import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core'
import Icon from '../../../../atoms/Icon'
import { ICON_NAMES } from '../../../../../constants'
import { getField, getItems, resolveDetailFields } from '../fieldResolver'

export const useTaskRowStyles = makeStyles(() => ({
  list: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
    width: '100%',
    display: 'flex',
    flexDirection: 'column'
  },
  row: {
    display: 'flex',
    flexDirection: 'column',
    padding: 'var(--dim-2) 0',
    '&:not(:last-child)': {
      borderBottom: 'var(--border-neutral-thin)'
    }
  },
  rowHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--dim-2)'
  },
  expandBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    color: 'var(--color-neutral-500)'
  },
  label: {
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-neutral-800)'
  },
  labelDone: {
    fontSize: 'var(--font-size-sm)',
    textDecoration: 'line-through',
    color: 'var(--color-neutral-500)'
  },
  trailing: {
    marginLeft: 'auto',
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--dim-2)'
  },
  typeBadge: {
    fontSize: 'var(--font-size-xs)',
    padding: '0 var(--dim-1)',
    borderRadius: 'var(--border-radius-sm)',
    background: 'var(--color-neutral-200)',
    color: 'var(--color-neutral-800)'
  },
  detailFields: {
    display: 'flex',
    flexDirection: 'column',
    paddingLeft: 'var(--dim-6)',
    paddingTop: 'var(--dim-1)'
  },
  detailRow: {
    display: 'flex',
    gap: 'var(--dim-4)',
    padding: 'var(--dim-1) 0',
    alignItems: 'baseline'
  },
  detailLabel: {
    width: '100px',
    flexShrink: 0,
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-neutral-500)',
    fontWeight: 'var(--font-weight-light)'
  },
  detailValue: {
    flex: 1,
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-neutral-800)'
  },
  statusDone: {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-success-600)'
  },
  statusOpen: {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-neutral-500)'
  }
}))

export const detailFieldShape = PropTypes.arrayOf(PropTypes.shape({
  field: PropTypes.string.isRequired,
  label: PropTypes.string,
  format: PropTypes.string
}))

function DetailFields ({ item, detailFields, formatter, classes }) {
  const fields = resolveDetailFields(item, detailFields, formatter)
  if (!fields.length) return null

  return (
    <div className={classes.detailFields}>
      {fields.map(({ label, value, format }, idx) => (
        <div key={idx} className={classes.detailRow}>
          <span className={classes.detailLabel}>{label}</span>
          <span className={classes.detailValue} style={format === 'text' ? { whiteSpace: 'pre-line' } : undefined}>
            {value}
          </span>
        </div>
      ))}
    </div>
  )
}

DetailFields.propTypes = {
  item: PropTypes.object.isRequired,
  detailFields: detailFieldShape,
  formatter: PropTypes.func.isRequired,
  classes: PropTypes.object.isRequired
}

export function TaskRow ({ item, displayConfig, detailFields, formatter, completedValue, classes }) {
  const [expanded, setExpanded] = useState(false)
  const label = getField(item, displayConfig, 'label')
  const status = getField(item, displayConfig, 'status')
  const type = getField(item, displayConfig, 'type')
  const isDone = status === completedValue
  const hasDetails = detailFields?.length > 0

  return (
    <li className={classes.row}>
      <div className={classes.rowHeader}>
        {hasDetails && (
          <button
            type='button'
            className={classes.expandBtn}
            onClick={() => setExpanded(v => !v)}
            aria-label={expanded ? 'Collapse' : 'Expand'}
          >
            <Icon
              name={expanded ? ICON_NAMES.chevronDown : ICON_NAMES.chevronRight}
              customSize={14}
            />
          </button>
        )}
        <span className={isDone ? classes.labelDone : classes.label}>
          {label}
        </span>
        <span className={classes.trailing}>
          {type && <span className={classes.typeBadge}>{type}</span>}
          <span className={isDone ? classes.statusDone : classes.statusOpen}>{status}</span>
        </span>
      </div>
      {hasDetails && expanded && (
        <DetailFields item={item} detailFields={detailFields} formatter={formatter} classes={classes} />
      )}
    </li>
  )
}

TaskRow.propTypes = {
  item: PropTypes.object.isRequired,
  displayConfig: PropTypes.object,
  detailFields: detailFieldShape,
  formatter: PropTypes.func.isRequired,
  completedValue: PropTypes.string.isRequired,
  classes: PropTypes.object.isRequired
}

/** Filter items, excluding internal rows for non-advisors. */
export function getVisibleItems (data, displayConfig, isAdvisor) {
  const items = getItems(data, displayConfig)
  if (isAdvisor) return items
  return items.filter(i => {
    const v = getField(i, displayConfig, 'isInternal')
    return v !== true && v !== 'true'
  })
}

export function getItemKey (item, displayConfig, idx) {
  return `${item.taskId ?? item.id ?? getField(item, displayConfig, 'label') ?? 'item'}-${idx}`
}

export { getCompletedValue, getField } from '../fieldResolver'
